import VpnKeyRounded from '@mui/icons-material/VpnKeyRounded';
import SmartToyRounded from '@mui/icons-material/SmartToyRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import { ArtButton, DynamicForm, FormService, useApiService, validators } from '@art/shared-ui';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { updateAppState } from '../../../store';
import { useNavigate } from 'react-router-dom';
import { useIDPContext } from '../../../auth';
import styles from '../onboarding.module.scss'
import { WorkspaceGlyph } from '../../utils/onBoarding_svg'

const PROJECT_INCLUDES = [
    { icon: <VpnKeyRounded />, text: 'API keys and channels, scoped to this project' },
    { icon: <SmartToyRounded />, text: 'Agents, knowledge bases and interceptors' },
    { icon: <InsightsRounded />, text: 'Usage, limits and activity you can track' },
];

export default function ProjectSetup({ user_data }: any) {

    const apiService = useApiService();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [formService, setFormService] = useState<FormService | null>(null);
    const { authGuardInstance } = useIDPContext();

    const [hasProjects, setHasProjects] = useState<boolean>(() => {
        return user_data?.tenant_info?.hasOwnProperty('number_of_projects') && user_data?.tenant_info?.number_of_projects > 0;
    });

    useEffect(() => {
        apiService.get("/projects").then((response: any) => {
            const projects = response.data?.data?.projects || [];
            setHasProjects(projects.length > 0);
        }).catch((err) => {
            console.error(err);
        });
    }, [apiService]);

    const formBuilder = (_data: any) => {
        return [

            {
                type: "row",
                children: [
                    {
                        type: "col",
                        classNames: ["col-12"],
                        children: [
                            ...[
                                !hasProjects ? {
                                    id: "title",
                                    label: "Project Name",
                                    type: "text",
                                    placeholder: "Enter your project name",
                                    required: false,
                                    dataQa: "artprojectcreate",
                                    value: "",
                                    validate: validators((value) => {
                                        if (!value || value.trim() === "") {
                                            return "Project name is required.";
                                        }
                                        if (/[^a-zA-Z0-9]/.test(value)) {
                                            return "Project name can only contain alphanumeric characters.";
                                        }
                                        if (value.length < 3 || value.length > 20) {
                                            return "Name must be between 3-20 characters"
                                        }
                                        return null;
                                    }),
                                } : {
                                    id: "project_key",
                                    label: "Project",
                                    type: "select",
                                    placeholder: "select project",
                                    required: false,
                                    getOptions: async () => {
                                        const response = await apiService.get("/projects/list-all");
                                        return response.data.data.projects.map((item: any) => ({
                                            label: item.title,
                                            value: item.project_key
                                        }));
                                    },
                                    dataQa: "artprojectselect",
                                    value: "",
                                    validate: validators((value) => {
                                        if (!value || value.trim() === "") {
                                            return "Project is required.";
                                        }
                                        return null;
                                    }),
                                }
                            ],
                        ],
                    },
                ],
            },
            {
                type: "row",
                children: [{
                    type: "col",
                    classNames: ["col-12"],
                    children: [{
                        id: "selected_environment",
                        label: "Environment",
                        type: "select",
                        placeholder: "Select environment",
                        // Options are loaded asynchronously and are scope-filtered. Do not
                        // render a previously saved value before it exists in that option
                        // set; MUI otherwise treats it as an invalid selection.
                        value: "",
                        getOptions: async () => {
                            const response = await apiService.get("/environments?limit=100");
                            return (response.data?.data?.environments || []).map((item: any) => ({
                                label: item.title,
                                value: item.title,
                            }));
                        },
                        validate: validators((value) => !value ? "Environment is required." : null),
                    }],
                }],
            },
        ];
    };

    const handleCreateProjectClick = () => {
        formService?.submit()
    }

    const saveProjectPreference = (data: any) => {
        if (data.hasOwnProperty('project_key') && data['project_key']) {
            updateApplicationState(data["project_key"], data["selected_environment"])
        } else {
            apiService.post("/projects", data).then((response: any) => {
                const projectData = response?.data?.data;
                if (projectData) {
                    updateApplicationState(projectData?.project_key, data["selected_environment"])
                } else {
                    apiService.showSnack("Something went wrong! Please try again.", "error")
                }
            }).catch((error) => {
                apiService.showSnack(error.response?.data?.message ?? "Something went wrong! Please try again.", "error")
            })
        }

    }

    const updateApplicationState = (project_key: string, selected_environment: string) => {
        if (!authGuardInstance) return;
        apiService.put('/user/application-state', {
            selected_project: project_key,
            selected_environment
        }, {
            headers: {
                ProjectKey: project_key,
                EnvironmentName: selected_environment,
            },
        }).then(({ data: { data } }) => {
            dispatch(updateAppState({
                project_key: data?.selected_project,
                project_name: data?.selected_project_name,
                environment: data?.selected_environment,
                is_invited: data?.is_invited,
            }))
            authGuardInstance.updateUser(data);
            navigate('/onboarding/review')
        }).catch(() => {
            apiService.showSnack("Something went wrong! Please try again.", "error")
        })
    }

    return (
        <section className={styles.split}>
            <div className={`${styles.split_copy} ${styles.reveal}`}>
                <h1 className={styles.display}>
                    {!hasProjects ? "Create your " : "Select your "}
                    <span className={styles.accent}>project.</span>
                </h1>

                <div className={styles.setup_kicker}>Let&rsquo;s get building.</div>

                <p className={styles.lede}>
                    A project is your dedicated space to build, manage and track real-time solutions
                    with A Realtime Tech. Everything you create lives inside it.
                </p>

                <ul className={styles.point_list}>
                    {PROJECT_INCLUDES.map(({ icon, text }) => (
                        <li key={text}>
                            <span className={styles.point_icon}>{icon}</span>
                            {text}
                        </li>
                    ))}
                </ul>
            </div>

            <div className={`${styles.reveal} ${styles.delay_2}`}>
                <div className={styles.setup_panel}>
                    <div className={styles.setup_panel_head}>
                        <WorkspaceGlyph />
                        <div>
                            <div className={styles.setup_panel_title}>
                                {!hasProjects ? "New project" : "Choose a project"}
                            </div>
                            <div className={styles.setup_panel_hint}>
                                {!hasProjects
                                    ? "3–20 characters, letters and numbers only"
                                    : "Pick the project you want to work in"}
                            </div>
                        </div>
                    </div>

                    <div className={styles.form_shell}>
                        <div className="container">
                            <DynamicForm key={hasProjects ? 'select' : 'create'} formBuilder={formBuilder}
                                onInit={(service: FormService) => setFormService(service)} onSubmit={saveProjectPreference} />
                        </div>

                        <div className={styles.setup_cta_wrap}>
                            <ArtButton
                                label={!hasProjects ? "Create Project" : "Select Project"}
                                dataQa="onboarding-create-project"
                                onClick={handleCreateProjectClick}
                                // shared-ui's MuiButton theme sets width:auto, which beats
                                // fullWidth — sx is applied last, so it has to go here
                                sx={{ py: 1.15, width: '100%' }}
                            />
                        </div>
                    </div>
                </div>

                <p className={styles.setup_footnote}>
                    You can rename this project or spin up more of them any time from Settings.
                </p>
            </div>
        </section>
    )
}
